import React, { useState, useContext } from "react";
import { LassieContext } from "../../context/LassieProvider";
import { getIconForTurbine } from "../../components/ol-map/icons";
import { toPrettyDateStr, formatInspectionDateToYMD } from "../../prettyDate";
import ArrowAnimation from "../arrow-animation/ArrowAnimation";
import redirectIcon from "../../assets/goto.png";
import "../../page/lightning/turbine-manager/turbine-manager.css";

export default function TurbineManagerTable({
  filteredTurbines,
  handleHeaderCheckboxChange,
  handleRowCheckboxChange,
  toggleSort,
  sortField,
  sortOrder,
  sortTurbines,
  seeMoreTurbineIds,
  setSeeMoreTurbineIds,
  renderManagedTurbines,
  visibleColumns,
  addDays,
}) {
  const { selectedTurbineIds } = useContext(LassieContext);
  const [subSortField, setSubSortField] = useState("timestamp");
  const [subSortOrder, setSubSortOrder] = useState("ASC");

  const toggleSubSort = (field) => {
    if (subSortField === field) {
      setSubSortOrder(subSortOrder === "ASC" ? "DESC" : "ASC");
    } else {
      setSubSortField(field);
      setSubSortOrder("ASC");
    }
  };

  const isHeaderCheckboxChecked = filteredTurbines.length > 0 &&
    filteredTurbines.every((ft) =>
      selectedTurbineIds.some((sel) => sel.id === ft.id)
    );

  return (
    <div className="table-container">
      <table className="turbine-manager-table">
        <thead>
          <tr>
            <th className="dropdown-header" ></th>
            <th className="checkbox-header">
              <input
                type="checkbox"
                checked={isHeaderCheckboxChecked}
                onChange={handleHeaderCheckboxChange}
              />
            </th>

            {visibleColumns.map((col) => (
              <th
                key={col.id}
                className={`${col.sortable ? "cursor-pointer" : ""} ${col.id === "name" ? "name-header" : ""}`.trim()}
                onClick={() => col.sortable && toggleSort(col.id)}
              >
                {col.label}{" "}
                {sortField === col.id
                  ? sortOrder === "ASC"
                    ? "↓"
                    : "↑"
                  : col.sortable
                    ? "↑↓"
                    : ""}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filteredTurbines.sort(sortTurbines).map((t, i) => {
            const sortedTurbineLightning = [...t.turbine_lightning].sort(
              (a, b) => {
                if (subSortField === "timestamp") {
                  const formattedA = `${toPrettyDateStr(
                    new Date(a.timestamp),
                    true
                  )}.${Math.round(a.nanosecond / 1e6)
                    .toString()
                    .padStart(3, "0")}`;
                  const formattedB = `${toPrettyDateStr(
                    new Date(b.timestamp),
                    true
                  )}.${Math.round(b.nanosecond / 1e6)
                    .toString()
                    .padStart(3, "0")}`;
                  return subSortOrder === "ASC"
                    ? formattedA.localeCompare(formattedB)
                    : formattedB.localeCompare(formattedA);
                }

                const compareA = a[subSortField];
                const compareB = b[subSortField];
                return subSortOrder === "ASC"
                  ? compareA - compareB
                  : compareB - compareA;
              }
            );

            return (
              <React.Fragment key={t.id}>
                <tr className="turbine-row">
                  {t.turbine_lightning.length !== 0 ? (
                    <td className="dropdown-row">
                      <ArrowAnimation
                        showMore={seeMoreTurbineIds.indexOf(t.id) !== -1}
                        setShowMore={(value) => {
                          if (seeMoreTurbineIds.indexOf(t.id) === -1) {
                            setSeeMoreTurbineIds((prev) => [...prev, t.id]);
                          } else {
                            setSeeMoreTurbineIds((prev) =>
                              prev.filter((p) => p !== t.id)
                            );
                          }
                        }}
                      />
                    </td>
                  ) : (
                    <td className="dropdown-row"></td>
                  )}
                  <td className="checkbox-row">
                    <input
                      type="checkbox"
                      checked={selectedTurbineIds.some((s) => s.id === t.id)}
                      onChange={(e) => handleRowCheckboxChange(e, t.id, i)}
                    />
                  </td>

                  {visibleColumns.map((col) => {
                    switch (col.id) {
                      case "name":
                        return (
                          <td className="name-column" key={col.id}>
                            <div
                              className="turbine-name-pill"
                              data-full-name={t.name.length > 7 ? t.name : null}
                              onClick={() => {
                                window.open(
                                  `/lassie/windfarm/${t.windfarm_id}/turbine/${t.id}`,
                                  "_blank"
                                );
                              }}
                            >
                              <p>{t.name}</p>
                              <img src={redirectIcon} className="clear-icon" alt='turbine name' />
                            </div>
                          </td>
                        );
                      case "status":
                        return (
                          <td key={col.id}>
                            <img
                              style={{ height: "1.5em" }}
                              src={getIconForTurbine(t, false)}
                              alt="status icon"
                            />
                          </td>
                        );
                      case "distance":
                        return renderManagedTurbines(
                          t,
                          `${Math.round(
                            Math.min(...t.turbine_lightning.map((l) =>
                              parseFloat(l.distance_km)
                            )) * 1000
                          )} m`,
                          undefined,
                          col.id
                        );

                      case "probability":
                        return renderManagedTurbines(
                          t,
                          `${Math.round(t.probability_of_atleast_one * 100)} %`,
                          undefined,
                          col.id
                        );

                      case "outside_iec":
                        return renderManagedTurbines(
                          t,
                          t.turbine_lightning.some((tl) => tl.outside_certification)
                            ? "✔"
                            : "N/A",
                          "value-checkmark",
                          col.id
                        );

                      case "model":
                        return <td key={col.id}>{t.model}</td>;
                      case "comment":
                        return (
                          <td
                            key={col.id}
                            className={t.user_comment ? "comment" : ""}
                            data-full-comment={t.user_comment || undefined}
                          >
                            {t.user_comment}
                          </td>
                        );
                      case "inspection_type":
                        return <td key={col.id}>{t.inspection?.type || ""}</td>;

                      case "inspection_method":
                        return <td key={col.id}>{t.inspection?.method || ""}</td>;

                      case "due_date":
                        return <td key={col.id}>{formatInspectionDateToYMD(t.inspection?.due_date) || ""}</td>;

                      case "date":
                        return <td key={col.id}>{formatInspectionDateToYMD(t.inspection?.date) || ""}</td>;

                      case "inspection_status":
                        return <td key={col.id}>{t.inspection?.status || ""}</td>;

                      case "marked_for_repair":
                        return <td key={col.id}>{t.marked_for_repair ? "✔" : ""}</td>;

                      case 'overdue_remind_in_days': {
                        const inspectionDate = new Date(t.inspection?.due_date);
                        const remindDays = t.inspection?.overdue_remind_in_days;

                        if (!inspectionDate || typeof remindDays !== 'number') {
                          return <td key={col.id}></td>;
                        }

                        const reminderDate = addDays(inspectionDate, remindDays);
                        const now = new Date();

                        const daysRemaining = Math.ceil((reminderDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

                        return (
                          <td key={col.id}>
                            {daysRemaining > 0
                              ? `In ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''}`
                              : `Sent ${toPrettyDateStr(reminderDate)}`}
                          </td>
                        );
                      }
                      default:
                        return null;
                    }
                  })}
                </tr>

                {seeMoreTurbineIds.indexOf(t.id) !== -1 && (
                  <>
                    <tr className="lightning-table">
                      <th></th>
                      <th></th>
                      <th
                        className="cursor-pointer"
                        onClick={() => toggleSubSort("distance_km")}
                      >
                        Distance{" "}
                        {subSortField === "distance_km"
                          ? subSortOrder === "ASC"
                            ? "↑"
                            : "↓"
                          : "↑↓"}
                      </th>
                      <th
                        className="cursor-pointer"
                        onClick={() => toggleSubSort("semi_major_axis")}
                      >
                        Semi major axis{" "}
                        {subSortField === "semi_major_axis"
                          ? subSortOrder === "ASC"
                            ? "↑"
                            : "↓"
                          : "↑↓"}
                      </th>
                      <th
                        className="cursor-pointer"
                        onClick={() => toggleSubSort("peak_current")}
                      >
                        Peak current{" "}
                        {subSortField === "peak_current"
                          ? subSortOrder === "ASC"
                            ? "↑"
                            : "↓"
                          : "↑↓"}
                      </th>
                      <th
                        className="cursor-pointer"
                        onClick={() => toggleSubSort("probability_of_hit")}
                      >
                        Probability{" "}
                        {subSortField === "probability_of_hit"
                          ? subSortOrder === "ASC"
                            ? "↑"
                            : "↓"
                          : "↑↓"}
                      </th>
                      <th>Outside IEC</th>
                      <th>Subsequent stroke</th>
                      <th
                        className="cursor-pointer"
                        onClick={() => toggleSubSort("timestamp")}
                      >
                        Timestamp{" "}
                        {subSortField === "timestamp"
                          ? subSortOrder === "ASC"
                            ? "↑"
                            : "↓"
                          : "↑↓"}
                      </th>
                    </tr>

                    {sortedTurbineLightning.map((tl) => (
                      <tr
                        className={`lightning-row ${tl.outside_certification
                          ? "lightning-outside-cert"
                          : ""
                          }`}
                        key={tl.id}
                      >
                        <td></td>
                        <td></td>
                        <td>{Math.round(tl.distance_km * 100) * 10} m</td>
                        <td>{Math.round(tl.semi_major_axis * 100) * 10} m</td>
                        <td>{Math.round(tl.peak_current * 10) / 10} kA</td>
                        <td>{Math.round(tl.probability_of_hit * 100)} %</td>
                        <td className="value-checkmark">
                          {tl.outside_certification ? "✔" : ""}
                        </td>
                        <td className="value-checkmark">
                          {tl.parent_id !== null ? "✔" : " "}
                        </td>
                        <td className="lightning-date">
                          {toPrettyDateStr(new Date(tl.timestamp), true)}.
                          {Math.round(tl.nanosecond / 1e6)
                            .toString()
                            .padStart(3, "0")}
                        </td>
                      </tr>
                    ))}
                  </>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div >
  );
}
